import { Component, Input, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  CellTypes,
  Column,
  Pagination,
  UtilService,
} from '@material-library';
import { Subscription } from 'rxjs';
import { ApiService } from 'src/app/core/api.service';
import {
  Device,
  DeviceListApiOutput,
  ErrCode,
  Session,
  User,
  UserSessionListApiOutput,
} from 'src/app/shared/models';
import * as fromRoot from '../../reducers';
import { PageEvent } from '@angular/material/paginator';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { SnackbarService } from 'src/app/shared/services/snackbar.service';
import { UserService } from 'src/app/core/user.service';
import { B64Pipe } from 'src/app/shared/pipes/b64.pipe';
import { ValidateService } from 'src/app/core/validate.service';



@Component({
  selector: 'app-devices',
  templateUrl: './devices.component.html',
  styleUrls: ['./devices.component.scss'],
})
export class DevicesComponent implements OnInit {
  public devicesAndSessionsDeepCopyItems: any[] = [];
  public devicesInChunk: Device[] = [];
  public devicesAndSessionsItemsOfSearch: any[] = [];
  public devicesPaginationConfig: Pagination;
  public isLoading: boolean = false;
  public searchValue: string = '';

  public devicesColumns: Column[] = [];
  public isActionLoading: boolean = false;
  public isDialogOpen: boolean = false;
  public deviceInputError: boolean = false;
  public pageObj = {
    pageIndex: 0,
    pageSize: 10,
    previousPageIndex: 0,
    length: 0,
    pageSizeOptions: [10, 20, 30, 40, 50, 100],
  };

  public sortByName: boolean = false;
  public sortedColumnKey: string = 'added';
  public isSortAsc: boolean = true;
  public showDeleted: boolean = false;
  public data: any;
  public dialog: any = { title: '', type: '', isOpen: false, displayName: '', action: '' };
  public errcode: ErrCode;
  public displayName: string;
  public nameInvalidError: string = '';
  public changeDevicePermissions: boolean;

  constructor(
    public b64Pipe: B64Pipe,
    private api: ApiService,
    private snackbarService: SnackbarService,
    private validateService: ValidateService,
    public utilService: UtilService,
  ) { }

  async ngOnInit() {

    this.devicesPaginationConfig = {
      length: 0,
      pageSize: 10,
      pageSizeOptions: [10, 20, 30, 40, 50, 100],
      alignment: 'right',
      onPageChange: (event: PageEvent) => this.handlePageChange(event),
    };

    this.devicesColumns = [
      {
        key: 'type',
        header: 'Type',
        type: CellTypes.text,
        cell: (element: Record<string, any>) =>
          `${element['device_name'] ||
          (element['is_mobile'] ? 'Mobile' : 'Desktop') ||
          '-'
          }`,
        isSortable: true,
        sort: (sortByName: boolean) => {
          this.sortByName = !this.sortByName;
          this.sortData('type', this.sortByName);
        },
      },
      {
        key: 'added',
        header: 'Added',
        type: CellTypes.date,
        isSecondary: true,
        cell: (element: Record<string, any>) => {
          return {
            value: this.utilService.formatDate(new Date(element['login_servtime'] || element['cre_servtime']), 'MMM d, y'),
            smSubtext: `${element['ipaddr'] || '-'}`
          }
        },
        isSortable: true,
        sort: (sortByRecentEntry: boolean) => {
          this.sortData('added', !sortByRecentEntry);
        },
      },
      {
        key: 'device',
        header: 'Device',
        subTextKey: 'description',
        isPrimary: true,
        type: CellTypes.text,
        cell: (element: Record<string, any>) => {
          const isCurrent = element['is_current'] === 1;
          const isDeleted = element['is_deleted'] === 1;
          const chipText = (isCurrent || isDeleted) ? `(${isCurrent ? 'Active Session' : 'Removed'})` : '';
          return {
            value: `${element['useragent_browser'] || element['name'] || '-'
              }`,
            subText: `${(element['useragent_version']
              ? 'Version ' + element['useragent_version'] +
              ' ' +
              element['useragent_platform']
              : '') ||
              (element['version']
                ? 'Version ' + element['version'] + ' ' + element['devicetype_name']
                : '') ||
              '-'
              }`,
            overlineText: `${element['device_name'] ||
              (element['is_mobile'] ? 'Mobile' : 'Desktop') ||
              '-'
              } ${chipText}`,
          };
        },
        isSortable: true,
        sort: (sortByName: boolean) => {
          this.sortByName = !this.sortByName;
          this.sortData('device', this.sortByName);
        },
      },
      {
        key: 'ip',
        header: 'IP',
        type: CellTypes.text,
        cell: (element: Record<string, any>) => `${element['ipaddr'] || '-'}`,
        isSortable: true,
        sort: (sortByName: boolean) => {
          this.sortByName = !this.sortByName;
          this.sortData('ip', this.sortByName);
        },
      },
      {
        key: 'action',
        header: 'Actions',
        type: CellTypes.action,
        isAlignRight: true,
        actionLimit: 0,
        shouldDisable: (element: Record<string, any>) => {
          const isCurrent = element['is_current'] === 1;
          return isCurrent;
        },
        chipConfig: (element: Record<string, any>) => {
          const isCurrent = element['is_current'] === 1;
          const isDeleted = element['is_deleted'] === 1;
          return {
            showChip: isCurrent || isDeleted,
            value: isCurrent ? 'Active Session' : 'Removed'
          }
        },
        actions: [{
          name: 'Rename',
          icon: 'rename',
          onClick: this.onRenameClick,
          shouldShow: (rowData) => {
            return !!rowData['devicetype_name'] && !rowData.is_deleted;
          },
        },
        {
          name: 'Permissions',
          icon: 'settings',
          onClick: this.onPermissionClick,
          shouldShow: (rowData) => {
            return !!rowData['devicetype_name'] && !rowData.is_deleted;
          },
          divider: true
        },
        {
          name: 'Remove Devices',
          icon: 'sign-out',
          onClick: this.onDeleteClick,
          shouldShow: (rowData) => {
            return !!rowData['devicetype_name'] && !rowData.is_deleted;
          },
        },
        {
          name: 'Sign out',
          icon: 'sign-out',
          onClick: this.onSignOutClick,
          shouldShow: (rowData) => {
            return !!rowData['useragent_browser'] && !rowData.is_current;
          },
        },
        {
          name: 'Restore device',
          icon: 'restore',
          onClick: this.onRestoreClick,
          shouldShow: (rowData) => {
            return rowData.is_deleted;
          },
        },
        ],
      },
    ];

    this.pageObj = {
      pageIndex: 0,
      pageSize: 10,
      previousPageIndex: 0,
      length: 0,
      pageSizeOptions: [10, 20, 30, 40, 50, 100],
    };
    await this.refreshDevices();
  }

  public getAllDevices = async () => {
    const devices = await this.loadDevices();
    const sessions = await this.loadSessions();
    this.devicesAndSessionsDeepCopyItems = [...(devices ?? []), ...(sessions ?? [])];
    this.devicesAndSessionsItemsOfSearch = [...this.devicesAndSessionsDeepCopyItems]
  }

  public async loadSessions(): Promise<Session[]> {
    const sessions = await this.api.execute<UserSessionListApiOutput>('usersessionlist', {});
    return sessions?.sessions
  }

  public async loadDevices(): Promise<Device[]> {
    const devices = await this.api.execute<DeviceListApiOutput>('devicelist', {});
    devices?.devices?.forEach(device => {
      device.name = this.b64Pipe.transform(device.name);
    });
    return devices?.devices
  }


  onSearch = (searchValue: string) => {
    this.searchValue = searchValue;
    const devicesAndSessionsItemsOfSearch = [...this.devicesAndSessionsDeepCopyItems]
    this.devicesAndSessionsItemsOfSearch = devicesAndSessionsItemsOfSearch.filter(
      (devicesItemsData) =>
        (devicesItemsData.name ||
          devicesItemsData.useragent_browser) &&
        (devicesItemsData.name || devicesItemsData.useragent_browser)
          .toLowerCase()
          .includes(searchValue.toLowerCase()));
    this.pageObj.pageIndex = 0;
    this.handlePageChange(this.pageObj)
  };

  onToggleShowHideDevice = async (event: MatSlideToggleChange) => {
    this.showDeleted = event.checked;
    this.pageObj.pageIndex = 0;
    this.handlePageChange(this.pageObj)
  }

  handlePageChange = (event: PageEvent) => {
    const currentPage = event.pageIndex + 1;
    const itemsPerPage = event.pageSize;

    const previousPageIndex = currentPage > 1 ? currentPage - 1 : 0;

    const startIndex =
      (currentPage == 0 ? currentPage : currentPage - 1) * itemsPerPage;
    let endIndex = startIndex + itemsPerPage;

    const filteredDevices = this.getFilterDataOnDeletedFlag();
    this.pageObj = {
      pageIndex: currentPage,
      pageSize: itemsPerPage,
      previousPageIndex: previousPageIndex,
      length: filteredDevices.length,
      pageSizeOptions: [10, 20, 30, 40, 50, 100],
    };

    this.devicesPaginationConfig.length = filteredDevices.length;

    if (endIndex > filteredDevices.length) {
      endIndex = filteredDevices.length;
    }
    this.devicesInChunk = [];
    this.devicesInChunk = filteredDevices.slice(startIndex, endIndex);
    this.devicesPaginationConfig = { ...this.devicesPaginationConfig }
  }

  getFilterDataOnDeletedFlag = () => {
    const devices = this.searchValue ? this.devicesAndSessionsItemsOfSearch : this.devicesAndSessionsDeepCopyItems
    if (this.showDeleted) {
      return devices;
    } else {
      return devices.filter(devicesItemsData => !devicesItemsData.is_deleted);
    }
  }
  setFilterDataOnDeletedFlag = (data: any[]) => {
    if (this.searchValue) {
      this.devicesAndSessionsItemsOfSearch = [...data];
    } else {
      this.devicesAndSessionsDeepCopyItems = [...data]
    }
  }

  sortData = (column: string, descending: boolean) => {
    let getProperty = (item: any) => {
      switch (column) {
        case 'type':
          return (
            item['device_name'] ||
            (item['is_mobile'] ? 'Mobile' : 'Desktop') ||
            '-'
          );
        case 'added':
          return item.login_servtime || item.cre_servtime || 0;
        case 'device':
          return item.useragent_browser || item.devicetype_name || '-';
        case 'ip':
          return item.ipaddr || '-';
        default:
          return '';
      }
    };
    const devices = this.searchValue ? this.devicesAndSessionsItemsOfSearch : this.devicesAndSessionsDeepCopyItems
    const sortedDevices = devices.sort((a, b) => {
      const beforeValue = getProperty(a);
      const afterValue = getProperty(b);

      if (typeof beforeValue === 'string' && typeof afterValue === 'string') {
        if (descending) {
          return afterValue.localeCompare(beforeValue);
        } else {
          return beforeValue.localeCompare(afterValue);
        }
      } else if (typeof beforeValue === 'number' && typeof afterValue === 'number') {
        if (descending) {
          return afterValue - beforeValue;
        } else {
          return beforeValue - afterValue;
        }
      } else {
        return 0; // Handle cases where types don't match or are not sortable
      }
    });


    this.setFilterDataOnDeletedFlag(sortedDevices);

    this.sortedColumnKey = column;
    this.isSortAsc = !descending;
    this.pageObj.pageIndex = 0;
    this.handlePageChange(this.pageObj)
  };


  validateDeviceName = (displayName: string) => {
    this.deviceInputError = false;
    this.displayName = displayName;
    this.validInput(displayName);
  }

  validInput(displayName: string): string {
    if (!this.validateService.isAlphaNumeric(displayName)) {
      this.nameInvalidError = 'Enter only alphanumeric characters.'
    } else if (!this.validateService.empty(displayName)) {
      this.nameInvalidError = 'Enter a valid device name';
      this.deviceInputError = true
    } else {
      this.nameInvalidError = '';
    }
    return this.nameInvalidError
  }

  onRenameClick = async (rowData: any) => {
    this.dialog = rowData;
    this.displayName = rowData.name;
    this.dialog.title = 'Rename device';
    this.dialog.type = 'rename';
    this.dialog.action = 'Save';
    this.isDialogOpen = true;
    this.deviceInputError = false;
    this.nameInvalidError = '';

  };

  onPermissionClick = async (rowData: any) => {
    this.displayName = rowData.name
    this.dialog = rowData;
    this.dialog.title = 'Device permissions';
    this.dialog.type = 'permission';
    this.dialog.action = 'Save'
    this.isDialogOpen = true;
    this.changeDevicePermissions =
      this.dialog.permission &&
        this.dialog.permission?.perCHANGEPASS
        ? true
        : false;
  };

  onDeleteClick = async (rowData: any) => {
    this.dialog = rowData;
    this.displayName = rowData.name;
    this.dialog.title = 'Remove device';
    this.dialog.type = 'delete';
    this.dialog.action = 'Remove'
    this.isDialogOpen = true;
  };

  onRestoreClick = async (rowData: any) => {
    this.dialog = rowData;
    this.displayName = rowData.name;
    this.dialog.title = 'Restore device';
    this.dialog.type = 'restore';
    this.dialog.action = 'Restore'
    this.isDialogOpen = true;
  };

  onSignOutClick = async (rowData: any) => {
    this.dialog = rowData;
    this.displayName = rowData.useragent_browser + ' ' + '(' + this.utilService.formatDate(new Date(rowData.lastaction_servtime), 'MMMM d, y') + ')';
    this.dialog.title = 'Sign out of web session';
    this.dialog.type = 'sign-out';
    this.dialog.action = 'Sign out'
    this.isDialogOpen = true;
  };


  onCancelClick = () => {
    this.isDialogOpen = false;
  };

  onActionClick = async () => {
    switch (this.dialog.type) {
      case 'rename':
        await this.onRenameSave();
        break;
      case 'permission':
        await this.onPermissionSave();
        break;
      case 'delete':
        await this.onDeleteSave();
        break;
      case 'restore':
        await this.onRestoreSave();
        break;
      case 'sign-out':
        await this.onSignOutSave();
        break;
      default:
        break;
    }
  };

  onRenameSave = async () => {
    this.deviceInputError = false;
    if (this.validateService.isEmpty(this.displayName)) {
      this.deviceInputError = true;
      return;
    }
    this.isActionLoading = true;
    try {
      await this.api.execute('devicerename', {
        device_id: this.dialog.id,
        devicename: this.displayName,
      });
      await this.refreshDevices();
      console.log('this.devicesAndSessionsDeepCopyItems', [...this.devicesAndSessionsDeepCopyItems]);
      console.log('this.devicesInChunk', [...this.devicesInChunk]);

      this.snackbarService.showMessage('success', 'Device Renamed.');
    } catch (error) {
      this.snackbarService.showMessage(
        'critical',
        "Couldn't complete action. Try again."
      );
    } finally {
      this.isActionLoading = false;
      this.isDialogOpen = false;
    }
  }

  refreshDevices = async () => {
    this.devicesInChunk = [];
    this.devicesAndSessionsItemsOfSearch = [];
    this.devicesAndSessionsDeepCopyItems = [];
    this.isLoading = true;
    await this.getAllDevices();
    this.pageObj.length = this.devicesAndSessionsDeepCopyItems.length;
    this.onSearch('');
    this.sortData('added', true);
    setTimeout(()=>{    this.devicesInChunk = [...this.devicesInChunk]},30);
    this.isLoading = false;
  }

  onPermissionSave = async () => {
    this.isActionLoading = true;
    try {
      await this.api.execute('devicepermission', {
        permission: 'perCHANGEPASS',
        device_id: this.dialog.id,
        value: this.changeDevicePermissions ? 1 : 0,
      });
      await this.refreshDevices();
      this.snackbarService.showMessage('success', 'Device permission updated.');
    } catch (error) {
      this.snackbarService.showMessage(
        'critical',
        "Couldn't complete action. Try again."
      );
    } finally {
      this.isActionLoading = false;
      this.isDialogOpen = false;
    }
  };

  onDevicePermissionChange = (changeDevicePermissions) => {
    this.changeDevicePermissions = changeDevicePermissions['checked'];
  };

  onDeleteSave = async () => {
    this.isActionLoading = true;
    try {
      await this.api.execute('devicedelete', {
        device_id: this.dialog.id
      });
      await this.refreshDevices();
      this.snackbarService.showMessage('success', 'Device Removed.');
    } catch (error) {
      this.snackbarService.showMessage(
        'critical',
        "Couldn't complete action. Try again."
      );
    } finally {
      this.isActionLoading = false;
      this.isDialogOpen = false;
    }
  }

  onRestoreSave = async () => {
    this.isActionLoading = true;
    try {
      await this.api.execute('devicerestore', {
        device_id: this.dialog.id
      });
      await this.refreshDevices();
      this.snackbarService.showMessage('success', 'Device Restored.');
    } catch (error) {
      this.snackbarService.showMessage(
        'critical',
        "Couldn't complete action. Try again."
      );
    } finally {
      this.isActionLoading = false;
      this.isDialogOpen = false;
    }
  }

  onSignOutSave = async () => {
    this.isActionLoading = true;
    try {
      await this.api.execute('usersessionkill', {
        id: this.dialog.id
      });
      await this.refreshDevices();
      this.snackbarService.showMessage('success', 'Signed out of web session.');
    } catch (error) {
      this.snackbarService.showMessage(
        'critical',
        "Couldn't complete action. Try again."
      );
    } finally {
      this.isActionLoading = false;
      this.isDialogOpen = false;
    }
  }

  get countText() {
    if (this.isLoading) {
      return ' ';
    }
    else {
      return `${this.devicesPaginationConfig.length} device${this.devicesPaginationConfig.length === 1 ? '' : 's'}`;
    }
  }
}
